import { Box, Stack, Typography, useTheme } from "@mui/material";
import { diffLines } from "diff";
import { useMemo } from "react";

interface InlineDiffViewerProps {
    oldText: string;
    newText: string;
    oldLabel?: string;
    newLabel?: string;
}

export const InlineDiffViewer = ({
    oldText,
    newText,
    oldLabel,
    newLabel,
}: InlineDiffViewerProps) => {
    const theme = useTheme();

    const changes = useMemo(
        () => diffLines(oldText, newText),
        [oldText, newText]
    );

    // Build line entries with gutter numbers
    const lines = useMemo(() => {
        const result: {
            type: "added" | "removed" | "unchanged";
            text: string;
            oldLine: number | null;
            newLine: number | null;
        }[] = [];
        let oldLine = 1;
        let newLine = 1;

        for (const change of changes) {
            const changeLines = change.value.replace(/\n$/, "").split("\n");
            for (const line of changeLines) {
                if (change.added) {
                    result.push({
                        type: "added",
                        text: line,
                        oldLine: null,
                        newLine: newLine++,
                    });
                } else if (change.removed) {
                    result.push({
                        type: "removed",
                        text: line,
                        oldLine: oldLine++,
                        newLine: null,
                    });
                } else {
                    result.push({
                        type: "unchanged",
                        text: line,
                        oldLine: oldLine++,
                        newLine: newLine++,
                    });
                }
            }
        }
        return result;
    }, [changes]);

    const hasChanges = changes.some((c) => c.added || c.removed);

    return (
        <Stack spacing={0}>
            {(oldLabel || newLabel) && (
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        px: 1.5,
                        py: 0.75,
                        bgcolor: "grey.100",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    {oldLabel && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: "error.main",
                                fontFamily: "monospace",
                            }}
                        >
                            --- {oldLabel}
                        </Typography>
                    )}
                    {newLabel && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: "success.dark",
                                fontFamily: "monospace",
                            }}
                        >
                            +++ {newLabel}
                        </Typography>
                    )}
                </Stack>
            )}
            <Box
                sx={{
                    maxHeight: 500,
                    overflowY: "auto",
                    overflowX: "auto",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    fontSize: "0.8125rem",
                    fontFamily: "monospace",
                    lineHeight: 1.6,
                }}
            >
                {!hasChanges ? (
                    <Box
                        sx={{
                            p: 2,
                            color: "text.secondary",
                            textAlign: "center",
                        }}
                    >
                        No changes
                    </Box>
                ) : (
                    <Box sx={{ width: "fit-content", minWidth: "100%" }}>
                        {lines.map((line, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "flex",
                                    width: "100%",
                                    bgcolor:
                                        line.type === "added"
                                            ? "rgba(46, 160, 67, 0.10)"
                                            : line.type === "removed"
                                              ? "rgba(248, 81, 73, 0.10)"
                                              : "transparent",
                                    "&:hover": { bgcolor: "action.hover" },
                                    whiteSpace: "pre",
                                }}
                            >
                                {/* Gutter: old line */}
                                <Box
                                    component="span"
                                    sx={{
                                        width: 44,
                                        minWidth: 44,
                                        textAlign: "right",
                                        pr: 0.5,
                                        color: "text.disabled",
                                        userSelect: "none",
                                        borderRight: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    {line.oldLine ?? ""}
                                </Box>
                                {/* Gutter: new line */}
                                <Box
                                    component="span"
                                    sx={{
                                        width: 44,
                                        minWidth: 44,
                                        textAlign: "right",
                                        pr: 0.5,
                                        color: "text.disabled",
                                        userSelect: "none",
                                        borderRight: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    {line.newLine ?? ""}
                                </Box>
                                {/* Prefix */}
                                <Box
                                    component="span"
                                    sx={{
                                        width: 20,
                                        minWidth: 20,
                                        textAlign: "center",
                                        color:
                                            line.type === "added"
                                                ? "success.dark"
                                                : line.type === "removed"
                                                  ? "error.main"
                                                  : "text.disabled",
                                        userSelect: "none",
                                        fontWeight: 700,
                                    }}
                                >
                                    {line.type === "added"
                                        ? "+"
                                        : line.type === "removed"
                                          ? "-"
                                          : " "}
                                </Box>
                                {/* Content */}
                                <Box
                                    component="span"
                                    sx={{
                                        flex: "0 0 auto",
                                        px: 0.5,
                                    }}
                                >
                                    {line.text || "\u00A0"}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Stack>
    );
};
